import React, { useEffect, useState } from 'react'
import { ChannelConfigType } from 'src/lib/types/channels'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'
import { Disclosure } from '@headlessui/react'
import { Disc, ChevronRight, Info } from 'react-feather'
import { useLocale } from 'src/lib/hooks/useLocal'

interface IntegrationChannelsProps {
  channels: ChannelConfigType[]
  onChange: (channels: any) => void
  children: JSX.Element
  hideChannels?: boolean
}

const IntegrationChannels: React.FC<IntegrationChannelsProps> = ({ channels, onChange, children, hideChannels }) => {
  const [channelConfig, setChannelConfig] = useState<any>([])

  const { t } = useLocale()

  useEffect(() => {
    if (!channels || channels.length === 0) return

    const localChannelConfig: any = []
    channels.forEach((channel: ChannelConfigType) => {
      const values = {
        id: channel.id,
        name: channel.name,
        description: channel.description,
        enabled: channel.enabled
      }
      localChannelConfig.push(values)
    })
    setChannelConfig(localChannelConfig)
  }, [channels])

  const handleChange = (id: string, checked: boolean) => {
    const channelIndex = channelConfig.findIndex((obj: any) => obj.id === id)
    const channelsDump = [...channelConfig]
    channelsDump[channelIndex].enabled = checked
    setChannelConfig(channelsDump)
    onChange(channelsDump)
  }

  const getCTAElement = (): JSX.Element => {
    const enabledChannelCount = channelConfig?.filter((channel: any) => channel?.enabled === true)?.length
    let CTAElement = (
      <p>
        Active on <span className='font-semibold text-primary-dark'>{enabledChannelCount}</span>{' '}
        {enabledChannelCount > 1 ? 'channels' : 'channel'}
      </p>
    )
    if (enabledChannelCount < 1) {
      CTAElement = (
        <p>
          <span className='text-[#C7981F] font-semibold'>Inactive</span> on all channels
        </p>
      )
      return CTAElement
    }
    if (enabledChannelCount === channelConfig?.length) {
      CTAElement = (
        <p>
          Active on <span className='font-semibold text-primary-dark'>all</span> channels
        </p>
      )
    }
    return CTAElement
  }

  if (!channels || channels.length === 0) return <></>

  return (
    <div className='flex flex-col bg-white border rounded border-gray-light'>
      <div className='flex flex-col gap-3 p-4'>
        {children}
        {channelConfig?.filter((channel: any) => channel?.enabled === true)?.length < 1 && !hideChannels && (
          <div className='inline-flex gap-2 text-sm border rounded border-[#C7981F66] bg-[#C7981F1A] w-full p-3 items-center'>
            <Info className='text-[#C7981F] w-4 h-4' />
            <p className='text-carbon'>{t('disabled_all_channels', { entity: 'integration' })}</p>
          </div>
        )}
      </div>
      {!hideChannels && (
        <div className='p-3 shadow-inner bg-gray-lightest shadow-gray-light'>
          <Disclosure>
            {({ open }) => (
              <>
                <Disclosure.Button className='flex items-center justify-start w-full'>
                  <ChevronRight className={`${open ? 'rotate-90 transform' : ''} h-5 w-5`} />
                  <div className='text-sm font-normal'>{getCTAElement()}</div>
                </Disclosure.Button>
                <Disclosure.Panel className='p-2'>
                  <ul>
                    {channels.map(channel => {
                      const isEnabled = channelConfig.find((obj: any) => obj.id === channel.id)?.enabled
                      return (
                        <li key={`${channel.id}-${channel.enabled}-${isEnabled}`}>
                          <FloCheckbox
                            value={isEnabled}
                            setValue={(checked: boolean) => handleChange(channel.id, checked)}
                            label={channel.name === 'Web' ? 'Website' : channel.name}
                            color='#4D4D4D'
                          />
                        </li>
                      )
                    })}
                  </ul>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        </div>
      )}
    </div>
  )
}

export default IntegrationChannels
